import React, { useState, useEffect } from 'react';
import { Autocomplete, Box, Typography, FormControl, Select, MenuItem, TextField, Checkbox, Grid } from '@mui/material';
import { Controller, useFormContext } from 'react-hook-form';
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';
import CheckBoxIcon from '@mui/icons-material/CheckBox';

const icon = <CheckBoxOutlineBlankIcon fontSize="small" />;
const checkedIcon = <CheckBoxIcon fontSize="small" />;

const ServiceSelectionComponent = ({ serviceList, categoryList, subCategoryList, providerServiceList }) => {
  const { control, watch, setValue, setError } = useFormContext();
  const [childServices, setChildServices] = useState([]);
  const servicesMasterId = watch('service');
  const categoryId = watch('serviceCategoryId');
  const subCategoryId = watch('subCategoryId');
  const services = watch('services') || [];

  // Get brand list with default fallback
  const getBrandList = (servicesMasterId) => {
    const brandList = serviceList.find(serv => serv.masterServiceId === servicesMasterId)?.brands;
    if (brandList && brandList.length > 0) {
      return brandList;
    } else {
      return [
        {
          "brandId": 1,
          "brandDetail": {
            "title": "General Brand",
          }
        } 
      ];
    }
  };
  // Get available brands for a specific child service
  const getAvailableBrandsForService = (childServiceId) => {
    const allBrands = getBrandList(servicesMasterId);
    
    // Get existing brands for this child service from providerServiceList
    const existingServices = providerServiceList?.filter(service => 
      service.servicesChildId === childServiceId
    ) || [];
    
    if (existingServices.length === 0) {
      // No existing services, all brands are available (excluding default brand ID 1)
      return allBrands.filter(brand => brand.brandId !== 1);
    }

    // Get existing brand IDs
    const existingBrandIds = existingServices.map(service => service.brand?.id).filter(Boolean);
    
    // Filter out existing brands and default brand
    const availableBrands = allBrands.filter(brand => 
      brand.brandId !== 1 && // Exclude default brand
      !existingBrandIds.includes(brand.brandId)
    );

    return availableBrands;
  };

  // Check if a child service has available brands (including default brand fallback)
  const hasAvailableBrands = (childServiceId) => {
    const availableBrands = getAvailableBrandsForService(childServiceId);
    
    // If no specific brands are available, check if we can use the default brand
    if (availableBrands.length === 0) {
      // Check if this child service already exists with default brand in providerServiceList
      const existingDefaultBrand = providerServiceList?.some(service => 
        service.servicesChildId === childServiceId && service.brand?.id === 1
      );
      
      // If default brand is not already used, then this service has available brands (default)
      return !existingDefaultBrand;
    }
    
    return availableBrands.length > 0;
  };

  // // Get available brands for a specific child service
  // const getAvailableBrandsForService = (childServiceId) => {
  //   const allBrands = getBrandList(servicesMasterId);
    
  //   // Get existing brands for this child service from providerServiceList
  //   const existingServices = providerServiceList?.filter(service => 
  //     service.servicesChildId === childServiceId
  //   ) || [];
    
  //   if (existingServices.length === 0) {
  //     // No existing services, all brands are available (excluding default brand ID 1)
  //     return allBrands.filter(brand => brand.brandId !== 1);
  //   }

  //   // Get existing brand IDs
  //   const existingBrandIds = existingServices.map(service => service.brand?.id).filter(Boolean);
    
  //   // Filter out existing brands and default brand
  //   const availableBrands = allBrands.filter(brand => 
  //     brand.brandId !== 1 && // Exclude default brand
  //     !existingBrandIds.includes(brand.brandId)
  //   );

  //   return availableBrands;
  // };

  // // Check if a child service has available brands
  // const hasAvailableBrands = (childServiceId) => {
  //   const availableBrands = getAvailableBrandsForService(childServiceId);
  //   return availableBrands.length > 0;
  // };

  useEffect(() => {
    if (servicesMasterId) {
      const selectedService = serviceList.find(serv => serv.masterServiceId === servicesMasterId);
      const allChildServices = selectedService?.childServices || [];
      
      // Filter child services to only show those with available brands
      const servicesWithAvailableBrands = allChildServices.filter(childService => 
        hasAvailableBrands(childService.childServiceId)
      );
      
      setChildServices(servicesWithAvailableBrands);
    } else {
      setChildServices([]);
    }
  }, [servicesMasterId, serviceList, providerServiceList]);

  const handleServiceSelection = (newValue) => {
    // Get existing services for the current master service (to preserve their data)
    const existingServicesForMaster = services.filter(service => 
      service.servicesMasterId === servicesMasterId
    );
    
    // Get services from other master services (keep them unchanged)
    const servicesFromOtherMasters = services.filter(service => 
      service.servicesMasterId !== servicesMasterId
    );

    // Process the new selections
    const selectedChildServices = newValue.map((service, index) => {
      // Check if this specific service already exists in our current services
      const existingService = existingServicesForMaster.find(s => 
        s.servicesChildId === service.childServiceId
      );

      // If it exists, preserve all its data including ServiceBrands
      if (existingService) {
        return existingService;
      }

      // If it's new, create with default values
      const newService = {
        servicesMasterId: servicesMasterId,
        servicesChildId: service.childServiceId,
        categoryId: categoryId,
        subCategoryId: subCategoryId,
        categoryTitle: categoryList.find(cat => cat.id === categoryId)?.title,
        subCategoryTitle: subCategoryList.find(subCat => subCat.id === subCategoryId)?.title,
        masterServiceTitle: serviceList.find(s => s.masterServiceId === servicesMasterId)?.title,
        childServiceTitle: service.title,
        ServiceBrands: service.ServiceBrands || [], // Keep existing ServiceBrands if any
        availableBrands: getAvailableBrandsForService(service.childServiceId) // Store available brands for this service
      };
      
      // Set validation error for new services that don't have brands selected
      setTimeout(() => {
        const newServiceIndex = servicesFromOtherMasters.length + index;
        setError(`services.${newServiceIndex}.brands`, {
          type: 'required',
          message: 'At least one variation must be selected'
        });
      }, 0);
      
      return newService;
    });
      
    // Combine services from other masters with the updated services for current master
    const updatedServices = [...servicesFromOtherMasters, ...selectedChildServices];
    
    setValue('services', updatedServices);
  };

  const getSelectedChildServices = () => {
    return services
      .filter(service => service.servicesMasterId === servicesMasterId)
      .map(service => childServices.find(cs => cs.childServiceId === service.servicesChildId))
      .filter(Boolean);
  };

  return (
    <>
      <Grid item xs={12} sm={6}>
        <Typography sx={{fontSize:'0.75rem', fontWeight: 600}}>Services</Typography>
        <Controller
          name='service'
          control={control}
          render={({ field, fieldState: { error } }) => (
            <FormControl fullWidth>
              <Select
                {...field}
                size='small'
                sx={{
                  fontSize: '0.75rem',
                  '& .MuiSelect-select': {
                    display: 'flex',
                    alignItems: 'center',
                  },
                }}
                error={!!error}
                disabled={!watch('subCategoryId')}
              >
                {serviceList.map((service) => (
                  <MenuItem sx={{fontSize:'0.75rem'}} key={service.masterServiceId} value={service.masterServiceId}>
                    {service.title}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
        />
      </Grid>
      <Grid item xs={12} sm={6}>
        <Typography sx={{fontSize:'0.75rem', fontWeight: 600}}>Child Services</Typography>
        <Controller
          name='services'
          control={control}
          rules={{}}
          render={({ field }) => (
            <Autocomplete
              {...field}
              sx={{
                '& .MuiAutocomplete-input': {
                  fontSize: '0.75rem',
                },
              }}
              disabled={!childServices.length > 0}
              multiple
              disableCloseOnSelect
              options={childServices}
              getOptionLabel={(option) => option.title}
              renderOption={(props, option, { selected }) => {
                const availableBrandCount = getAvailableBrandsForService(option.childServiceId).length;
                return (
                  <li {...props}>
                    <Checkbox
                      icon={icon}
                      checkedIcon={checkedIcon}
                      style={{ 
                        marginRight: 8,
                        transform: 'scale(0.8)',
                      }}
                      checked={selected}
                    />
                    <Box sx={{ flexGrow: 1 }}>
                      <Typography variant="body2" sx={{ fontSize: '0.75rem' }}>
                        {option.title}
                      </Typography>
                      <Typography variant="caption" sx={{ fontSize: '0.65rem', color: 'text.secondary' }}>
                        {availableBrandCount} variation{availableBrandCount !== 1 ? 's' : ''} available
                      </Typography>
                    </Box>
                  </li>
                );
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  size='small'
                  variant="outlined"
                  placeholder="Select services"
                />
              )}
              onChange={(_, newValue) => {
                handleServiceSelection(newValue);
              }}
              isOptionEqualToValue={(option, value) => option.childServiceId === value.childServiceId}
              value={getSelectedChildServices()}
              renderTags={() => null}
            />
          )}
        />
        {childServices.length === 0 && servicesMasterId && (
          <Typography sx={{ fontSize: '0.65rem', color: 'text.secondary', fontStyle: 'italic', mt: 0.5 }}>
            No child services with available brands found for this service.
          </Typography>
        )}
      </Grid>
    </>
  );
};

export default ServiceSelectionComponent;


// import React, { useState, useEffect } from 'react';
// import { Autocomplete, Box, Typography, FormControl, Select, MenuItem, TextField, Checkbox, Grid } from '@mui/material';
// import { Controller, useFormContext } from 'react-hook-form';
// import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';
// import CheckBoxIcon from '@mui/icons-material/CheckBox';

// const icon = <CheckBoxOutlineBlankIcon fontSize="small" />;
// const checkedIcon = <CheckBoxIcon fontSize="small" />;

// const ServiceSelectionComponent = ({ serviceList, categoryList, subCategoryList, providerServiceList }) => {
//   const { control, watch, setValue, setError  } = useFormContext();
//   const [childServices, setChildServices] = useState([]);
//   const servicesMasterId = watch('service');
//   const categoryId = watch('serviceCategoryId');
//   const subCategoryId = watch('subCategoryId');
//   const services = watch('services') || [];

//   useEffect(() => {
//     if (servicesMasterId) {
//       const selectedService = serviceList.find(serv => serv.masterServiceId === servicesMasterId);
//       setChildServices(selectedService?.childServices || []);
//     } else {
//       setChildServices([]);
//     }
//   }, [servicesMasterId, serviceList]);

//   const handleServiceSelection = (newValue) => {
//     // console.log("newValue", newValue);
    
//     // Get existing services for the current master service (to preserve their data)
//     const existingServicesForMaster = services.filter(service => 
//       service.servicesMasterId === servicesMasterId
//     );
    
//     // Get services from other master services (keep them unchanged)
//     const servicesFromOtherMasters = services.filter(service => 
//       service.servicesMasterId !== servicesMasterId
//     );

//     // Process the new selections
//     const selectedChildServices = newValue.map((service,index) => {
//       // Check if this specific service already exists in our current services
//       const existingService = existingServicesForMaster.find(s => 
//         s.servicesChildId === service.childServiceId
//       );

//       // If it exists, preserve all its data including ServiceBrands
//       if (existingService) {
//         return existingService;
//       }

//       // If it's new, create with default values
//       const newService = {
//         servicesMasterId: servicesMasterId,
//         servicesChildId: service.childServiceId,
//         categoryId: categoryId,
//         subCategoryId: subCategoryId,
//         categoryTitle: categoryList.find(cat => cat.id === categoryId)?.title,
//         subCategoryTitle: subCategoryList.find(subCat => subCat.id === subCategoryId)?.title,
//         masterServiceTitle: serviceList.find(s => s.masterServiceId === servicesMasterId)?.title,
//         childServiceTitle: service.title,
//         ServiceBrands: service.ServiceBrands || [] // Keep existing ServiceBrands if any
//       };
//       // Set validation error for new services that don't have brands selected
//         setTimeout(() => {
//           const newServiceIndex = servicesFromOtherMasters.length + index;
//           setError(`services.${newServiceIndex}.brands`, {
//             type: 'required',
//             message: 'At least one variation must be selected'
//           });
//         }, 0);
//         return newService;
//       });
      
//     // Combine services from other masters with the updated services for current master
//     const updatedServices = [...servicesFromOtherMasters, ...selectedChildServices];
    
//     // console.log("Updated services with preserved brands:", updatedServices);
//     setValue('services', updatedServices);
//   };

//   const getSelectedChildServices = () => {
//     return services
//       .filter(service => service.servicesMasterId === servicesMasterId)
//       .map(service => childServices.find(cs => cs.childServiceId === service.servicesChildId))
//       .filter(Boolean);
//   };

//   const isServiceAlreadySelected = (childServiceId) => {
//     return providerServiceList?.some(service => service.servicesChildId === childServiceId);
//   };

//   // const getBrandList = (servicesMasterId) => {
//   //   const brandList = serviceList.find(serv => serv.masterServiceId === servicesMasterId)?.brands;
//   //   if (brandList && brandList.length > 0) {
//   //     return brandList;
//   //   } else {
//   //     return [
//   //       {
//   //         "brandId": 1,
//   //         "brandDetail": {
//   //           "title": "General Brand",
//   //         }
//   //       } 
//   //     ];
//   //   }
//   // };
//   //  // Function to filter available brands for a child service
 
//   // const isServiceAlreadySelected = (childServiceId) => {
//   //   console.log("getBrandList",   getBrandList(servicesMasterId));
//   //   let selctedServices = providerServiceList?.filter(service => service.servicesChildId === childServiceId);
//   //   console.log("selctedServices",selctedServices);
//   //   if(selctedServices.length > 0){
//   //     const existingBrands = selctedServices.map(service => service.brand);
//   //     console.log(" get existing brands", existingBrands);
//   //     // Fixed: Compare brandId from getBrandList with id from existingBrands
//   //   const existingBrandIds = existingBrands.map(brand => brand.id); // Extract just the IDs
//   //   const availableBrands = getBrandList(servicesMasterId).filter(brand => 
//   //     brand.brandId !== 1 && // Exclude default brand (Generic)
//   //     !existingBrandIds.includes(brand.brandId) // Compare brandId with existing brand ids
//   //   );
//   //     console.log("availableBrands",availableBrands);
//   //     return availableBrands.length === 0;
//   //   }else{
//   //     return false
//   //   }    
//   // };
//   return (
//     <>
//       <Grid item xs={12} sm={6} >
//         <Typography sx={{fontSize:'0.75rem', fontWeight: 600 }}>Services</Typography>
//         <Controller
//           name='service'
//           control={control}
//           render={({ field, fieldState: { error } }) => (
//             <FormControl fullWidth>
//               <Select
//                 {...field}
//                 size='small'
//                 sx={{
//                   fontSize: '0.75rem',
//                   '& .MuiSelect-select': {
//                     display: 'flex',
//                     alignItems: 'center',
//                   },
//                 }}
//                 error={!!error}
//                 disabled={!watch('subCategoryId')}
//               >
//                 {serviceList.map((service) => (
//                   <MenuItem sx={{fontSize:'0.75rem'}} key={service.masterServiceId} value={service.masterServiceId}>{service.title}</MenuItem>
//                 ))}
//               </Select>
//             </FormControl>
//           )}
//         />
//       </Grid>
//       <Grid item xs={12} sm={6} >
//         <Typography sx={{fontSize:'0.75rem', fontWeight: 600 }}>Child Services</Typography>
//         <Controller
//           name='services'
//           control={control}
//           rules={{}}
//           render={({ field }) => (
//             <Autocomplete
//               {...field}
//               sx={{
//                 '& .MuiAutocomplete-input': {
//                   fontSize: '0.75rem',
//                 },
//               }}
//               disabled={!childServices.length>0}
//               multiple
//               disableCloseOnSelect
//               options={childServices}
//               // options={childServices.filter(service => !isServiceAlreadySelected(service.childServiceId))}
//               getOptionLabel={(option) => option.title}
//               renderOption={(props, option, { selected }) => (
//                 <li {...props}>
//                   <Checkbox
//                     icon={icon}
//                     checkedIcon={checkedIcon}
//                     style={{ 
//                       marginRight: 8,
//                       transform: 'scale(0.8)',
//                     }}
//                     checked={selected}
//                   />
//                   <Typography variant="body2" sx={{ fontSize: '0.75rem' }}>
//                     {option.title}
//                   </Typography>
//                 </li>
//               )}
//               renderInput={(params) => (
//                 <TextField
//                   {...params}
//                   size='small'
//                   variant="outlined"
//                   placeholder="Select services"
//                 />
//               )}
//               onChange={(_, newValue) => {
//                 handleServiceSelection(newValue);
//               }}
//               isOptionEqualToValue={(option, value) => option.childServiceId === value.childServiceId}
//               value={getSelectedChildServices()}
//               renderTags={()=>null}
//             />
//           )}
//         />
//       </Grid>
//     </>
//   );
// };

// export default ServiceSelectionComponent;